import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { Grid, IconButton, Skeleton, Typography } from "@mui/material";
import clsx from "clsx";
import { ExternalLink } from "components/Common/Links";
import { QuantityAdjuster } from "components/Common/QuantityAdjuster";
import { HardwareImage } from "components/Hardware/HardwareImage";
import { selectProducts } from "features/hardware/products";
import { useAppSelector } from "hooks/hooks";
import { useEffect, useState } from "react";
import { Availability, PriceInfo } from "types";
import { HardwarePurchaseType } from "utilities/constants/enums";
import { formatCostString } from "utilities/currency/numberFormattingUtility";
import { calculateHardwarePrice } from "utilities/hardwareUtils/hardwareCostUtils";
import { StockIndicator } from "../../HardwareBundles/StockIndicator";
import styles from "./SelectedHardwareListItem.module.scss";

interface SelectedHardwareListItemProps {
	imageUrl: string;
	displayName: string;
	sku: string;
	description: string;
	url: string;
	priceInfo: PriceInfo;
	availability: Availability;
	count: number;
	deleteItem: () => void;
	incrementCount: () => void;
	decrementCount: () => void;
	hardwarePurchaseType: HardwarePurchaseType;
}

export const SelectedHardwareListItem = ({
	imageUrl,
	displayName,
	sku,
	description,
	url,
	priceInfo,
	availability,
	deleteItem,
	decrementCount,
	incrementCount,
	count,
	hardwarePurchaseType,
}: SelectedHardwareListItemProps) => {
	const { isLoading: isProductsLoading } = useAppSelector(selectProducts);
	const [inTransition, setTransition] = useState(true);

	useEffect(() => {
		setTimeout(() => {
			setTransition(false);
		}, 10);
	}, []);

	if (isProductsLoading) {
		return (
			<Grid container className={styles.skeletonContainer}>
				<Skeleton variant="rectangular" height={200} />
			</Grid>
		);
	}

	const { price, subText } = calculateHardwarePrice(priceInfo, hardwarePurchaseType);

	return (
		<Grid container className={styles.border}>
			<Grid
				key={sku}
				container
				className={clsx(styles.cartItem, {
					[styles.transition]: inTransition,
				})}
			>
				<IconButton onClick={deleteItem} className={styles.removeIconContainer}>
					<HighlightOffIcon className={styles.removeIcon} />
				</IconButton>
				<Grid item className={styles.imageAndPriceContainer}>
					<HardwareImage productId={sku} imageUrl={imageUrl} size="x_small" />
					{priceInfo && (
						<Grid container>
							<Typography variant="body1" className={styles.price}>
								{formatCostString("", price, ",-")}
							</Typography>
							<Typography variant="description" color="text.secondary">
								{subText}
							</Typography>
						</Grid>
					)}
				</Grid>
				<Grid container>
					<Grid item>
						<Typography variant="body1" className={styles.truncate}>
							{displayName}
						</Typography>
						<Typography
							variant="description"
							color="text.secondary"
							component="div"
							className={styles.truncate}
						>
							{description.substring(displayName.length)}
						</Typography>
					</Grid>
					<Grid container className={styles.stockAndItemSwapContainer}>
						<StockIndicator
							idToSwap={sku}
							availability={availability}
							requestedQuantity={count}
							variant="horizontal"
						/>
					</Grid>
					<Grid container className={styles.productLink}>
						<ExternalLink href={url} textDecoration="underline" target="_blank">
							View specification
						</ExternalLink>
					</Grid>
					<Grid className={styles.descriptionAndCounterContainer}>
						<Grid container item mt={2}>
							<QuantityAdjuster
								quantity={count}
								up={incrementCount}
								down={decrementCount}
							/>
						</Grid>
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	);
};
